// Shared "correct an already-approved invoice" path. An approved invoice snapshot is
// locked by the Firestore rules; the one way to change it is a finance-only write that
// carries a correctionReason and is logged in invoice_snapshot_corrections. Every
// approval screen uses these helpers so the behaviour is the same everywhere.

// Thrown when an invoice that is already approved is approved again with different
// figures. A harmless re-click with identical figures is a no-op instead.
export class InvoiceOverrideRequiredError extends Error {
  constructor(details) {
    super(`${details.invoiceNumber} is already approved for PKR ${Math.round(details.existingAmount || 0).toLocaleString("en-PK")} — the new figures come to PKR ${Math.round(details.newAmount || 0).toLocaleString("en-PK")}.`);
    this.name = "InvoiceOverrideRequiredError";
    this.details = details;
  }
}

const rounded = (value) => Math.round(Number(value || 0));

// fields: [[key, label], …] -> the ones whose rounded value differs, as { label, from, to }.
export function figureChanges(fields, before, after) {
  return fields
    .map(([key, label]) => ({ key, label, from: rounded(before?.[key]), to: rounded(after?.[key]) }))
    .filter((change) => change.from !== change.to);
}

// The audit row written to invoice_snapshot_corrections.
export function buildCorrectionRecord({ invoiceNumber, clientCode, monthKey, previousAmount, newAmount, changes, reason, user, correctedAt }) {
  const text = String(reason || "").trim();
  if (!text) throw new Error("A reason is required to correct an already-approved invoice.");
  return {
    kind: "INVOICE_CORRECTED",
    invoiceNumber,
    clientCode,
    monthKey,
    previousAmount: rounded(previousAmount),
    newAmount: rounded(newAmount),
    changes: (changes || []).map(({ label, from, to }) => ({ label, from, to })),
    reason: text,
    correctedBy: user?.uid || null,
    correctedByEmail: user?.email || null,
    correctedAt
  };
}

const pkr = (value) => `PKR ${rounded(value).toLocaleString("en-PK")}`;

export function describeOverride(details) {
  const lines = (details.changes || []).map((change) => `  • ${change.label}: ${pkr(change.from)} → ${pkr(change.to)}`);
  return `${details.invoiceNumber} is already approved for ${pkr(details.existingAmount)}.\n\n`
    + `The new figures come to ${pkr(details.newAmount)}.\n`
    + (lines.length ? `\nWhat changes:\n${lines.join("\n")}\n` : "")
    + "\nThis replaces the approved invoice (receivable, P&L and reconciliation) with the new figures. The old figures are kept in the correction log. Continue?";
}

// Browser prompt for the reason. Returns the reason, or "" when the user backs out.
export function askCorrectionReason(error) {
  if (!window.confirm(describeOverride(error.details))) return "";
  return (window.prompt("Briefly describe why this invoice is being corrected (required):") || "").trim();
}

// Runs run({ force, correctionReason }); if it reports that the invoice is already approved
// with different figures, asks the operator (old vs new, then a reason) and runs it again as
// a correction. Any other error is passed on.
export async function runWithCorrectionPrompt(run) {
  try {
    return await run({ force: false, correctionReason: "" });
  } catch (error) {
    if (!(error instanceof InvoiceOverrideRequiredError)) throw error;
    const reason = askCorrectionReason(error);
    if (!reason) throw new Error("Correction cancelled — a reason is required, and the existing approved invoice is unchanged.");
    return run({ force: true, correctionReason: reason });
  }
}
